// ─── Statistical Plausibility Validator ───────────────────────────────────────
// Audits computed results for patterns that real reviewers would flag as
// suspicious: over-significance, implausible R², uniform VIFs, excessive alpha,
// perfectly symmetric correlations, and other "too clean" artefacts.

import { getDomainProfile } from './domainRealismProfiles'

// ── Helpers ───────────────────────────────────────────────────────────────────

function std(arr) {
  if (arr.length < 2) return 0
  const m = arr.reduce((s, v) => s + v, 0) / arr.length
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1))
}

function range(arr) {
  return Math.max(...arr) - Math.min(...arr)
}

// ── Plausibility checks ───────────────────────────────────────────────────────

function checkOLS(result, profile, warnings) {
  const coefs = Object.entries(result.coefficients).filter(([k]) => k !== 'const')
  if (coefs.length === 0) return

  // 1. Excessive significance rate
  const sigRate = coefs.filter(([, c]) => c.significant).length / coefs.length
  const expectedSigRate = profile.significanceRate
  if (sigRate > expectedSigRate + 0.30) {
    warnings.push({
      type: 'EXCESS_SIGNIFICANCE',
      severity: 'high',
      message: `${Math.round(sigRate * 100)}% of predictors are statistically significant. The ${profile.label} domain typically shows ~${Math.round(expectedSigRate * 100)}%. Over-significance suggests unrealistic data.`,
      metric: sigRate,
    })
  }

  // 2. R² too high
  const [r2Min, r2Max] = profile.typicalR2Range
  if (result.r_squared > r2Max + 0.10) {
    warnings.push({
      type: 'HIGH_R2',
      severity: 'medium',
      message: `R² = ${result.r_squared} exceeds the plausible range for ${profile.label} research (typical: ${r2Min}–${r2Max}). Real social science models rarely explain this proportion of variance without overfitting.`,
      metric: result.r_squared,
    })
  }

  // 3. R² too low to be credible (model may be misspecified)
  if (result.r_squared < r2Min - 0.05 && result.f_pvalue < 0.05) {
    warnings.push({
      type: 'VERY_LOW_R2',
      severity: 'low',
      message: `R² = ${result.r_squared} is below the typical floor for this domain. Ensure key explanatory variables are included.`,
      metric: result.r_squared,
    })
  }

  // 4. Uniform VIF (suspiciously identical)
  const vifVals = Object.values(result.vif || {}).filter(v => v != null && isFinite(v))
  if (vifVals.length >= 3 && std(vifVals) < 0.08) {
    warnings.push({
      type: 'UNIFORM_VIF',
      severity: 'low',
      message: `VIF values are suspiciously uniform (SD = ${std(vifVals).toFixed(3)}). Real predictor sets show natural variance in multicollinearity.`,
      metric: std(vifVals),
    })
  }

  // 5. All p-values extremely small
  const pVals = coefs.map(([, c]) => c.p_value)
  if (pVals.every(p => p < 0.001)) {
    warnings.push({
      type: 'ALL_P_TINY',
      severity: 'medium',
      message: 'Every predictor has p < .001. This pattern is extremely rare in real social science research and suggests artificially strong signal.',
    })
  }

  // 6. Suspiciously symmetric confidence intervals
  const ciWidths = coefs.map(([, c]) => c.ci_upper - c.ci_lower)
  if (ciWidths.length >= 3 && std(ciWidths) < 0.01) {
    warnings.push({
      type: 'UNIFORM_CI',
      severity: 'low',
      message: 'Confidence intervals are nearly identical in width — real models show variation reflecting different predictor variances.',
    })
  }

  // 7. Overly stable (near-identical) coefficients
  const betas = coefs.map(([, c]) => Math.abs(c.beta))
  if (betas.length >= 3 && std(betas) < 0.04) {
    warnings.push({
      type: 'UNIFORM_BETAS',
      severity: 'low',
      message: 'Effect sizes (β) are suspiciously uniform. Real predictors have meaningfully different effect magnitudes.',
    })
  }
}

function checkCorrelation(result, warnings) {
  if (!result.matrix || !result.variables) return
  const vars = result.variables

  // 1. Excessively high IV-IV correlations (potential multicollinearity)
  const highCorrs = []
  for (let i = 0; i < vars.length - 1; i++) {
    for (let j = i + 1; j < vars.length - 1; j++) {
      const r = result.matrix[vars[i]]?.[vars[j]]
      if (r != null && Math.abs(r) > 0.80) {
        highCorrs.push(`${vars[i]}↔${vars[j]}: r = ${r}`)
      }
    }
  }
  if (highCorrs.length > 0) {
    warnings.push({
      type: 'HIGH_IV_CORRELATION',
      severity: 'high',
      message: `Problematic multicollinearity detected: ${highCorrs.join('; ')}. Correlations above .80 between predictors undermine regression interpretation.`,
    })
  }

  // 2. All positive correlations (suspiciously harmonious)
  const allR = []
  for (let i = 0; i < vars.length; i++) {
    for (let j = i + 1; j < vars.length; j++) {
      const r = result.matrix[vars[i]]?.[vars[j]]
      if (r != null) allR.push(r)
    }
  }
  if (allR.length >= 3 && allR.every(r => r > 0)) {
    warnings.push({
      type: 'ALL_POSITIVE_CORR',
      severity: 'medium',
      message: 'All bivariate correlations are positive — theoretically implausible for most real datasets. Domain contradictions typically produce at least one negative or near-zero relationship.',
    })
  }

  // 3. Correlations suspiciously evenly spaced
  const sortedR = [...allR].sort((a, b) => a - b)
  const gaps = sortedR.slice(1).map((r, i) => r - sortedR[i])
  if (gaps.length >= 3 && std(gaps) < 0.02) {
    warnings.push({
      type: 'REGULAR_CORR_SPACING',
      severity: 'low',
      message: 'Correlation values are suspiciously evenly distributed. Real correlation structures show irregular spacing.',
    })
  }
}

function checkCronbach(result, warnings) {
  if (!result || result.alpha == null) return

  if (result.alpha > 0.97) {
    warnings.push({
      type: 'EXCESSIVE_CRONBACH',
      severity: 'medium',
      message: `α = ${result.alpha} is implausibly high. Values above .95 indicate item redundancy, not strong reliability. Reviewers expect α in the .70–.90 range for well-constructed scales.`,
      metric: result.alpha,
    })
  }

  // All item-total correlations nearly identical
  const itCorrs = Object.values(result.item_total_correlations || {}).filter(v => v != null)
  if (itCorrs.length >= 3 && std(itCorrs) < 0.02) {
    warnings.push({
      type: 'UNIFORM_ITEM_CORRELATIONS',
      severity: 'low',
      message: 'Item-total correlations are nearly identical — real scale items have differential discrimination power.',
    })
  }
}

function checkDescriptive(result, warnings) {
  if (!result) return
  const vars = Object.values(result)

  // All variables have same standard deviation
  const sds = vars.map(v => v.std).filter(v => v != null)
  if (sds.length >= 3 && std(sds) < 0.05) {
    warnings.push({
      type: 'UNIFORM_SD',
      severity: 'low',
      message: 'All variables have nearly identical standard deviations — real research data shows meaningful variation in spread across constructs.',
    })
  }

  // Suspiciously zero skewness across all variables
  const skews = vars.map(v => v.skewness).filter(v => v != null)
  if (skews.length >= 3 && skews.every(s => Math.abs(s) < 0.05)) {
    warnings.push({
      type: 'ZERO_SKEWNESS',
      severity: 'low',
      message: 'All variables show near-zero skewness simultaneously — field data typically includes some distributional irregularity.',
    })
  }
}

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * Validate statistical results for research plausibility.
 *
 * @param {object} results - { ols, correlation, descriptive, cronbach }
 * @param {string} domain  - research domain
 * @returns {PlausibilityReport}
 */
export function validateStatisticalPlausibility(results, domain = 'public_policy') {
  const profile = getDomainProfile(domain)
  const warnings = []

  if (results.ols)         checkOLS(results.ols, profile, warnings)
  if (results.correlation) checkCorrelation(results.correlation, warnings)
  if (results.cronbach)    checkCronbach(results.cronbach, warnings)
  if (results.descriptive) checkDescriptive(results.descriptive, warnings)

  const highSeverityCount   = warnings.filter(w => w.severity === 'high').length
  const mediumSeverityCount = warnings.filter(w => w.severity === 'medium').length

  const plausibilityScore = Math.max(0, Math.min(100,
    100 - highSeverityCount * 25 - mediumSeverityCount * 10 - warnings.length * 3
  ))

  const recommendedAdjustments = []
  if (highSeverityCount > 0) {
    recommendedAdjustments.push('Re-generate dataset with stronger theory constraints applied (increase contradiction likelihood).')
  }
  const sigPattern = warnings.find(w => w.type === 'EXCESS_SIGNIFICANCE')
  if (sigPattern) {
    recommendedAdjustments.push(`Reduce expected effect strength for 1–2 predictors from 'strong' → 'moderate' or 'weak'.`)
  }
  if (warnings.find(w => w.type === 'HIGH_R2')) {
    recommendedAdjustments.push('Consider adding control variables that may absorb spurious variance, lowering R².')
  }
  if (warnings.find(w => w.type === 'ALL_POSITIVE_CORR')) {
    recommendedAdjustments.push('Add at least one theoretically justified negative predictor (e.g., bureaucratic inertia, complexity cost).')
  }

  return {
    plausibilityScore,
    suspicious: plausibilityScore < 70,
    warnings,
    highSeverityCount,
    mediumSeverityCount,
    recommendedAdjustments,
    verdict: plausibilityScore >= 85
      ? 'Plausible — findings pattern is consistent with real-world research norms.'
      : plausibilityScore >= 65
        ? 'Moderate concern — some patterns warrant attention before thesis submission.'
        : 'High concern — results display signatures of artificially clean data. Regenerate with stronger realism constraints.',
  }
}
